import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeader, parseResponse } from '../api/helpers';
import { pillBtn, filterInputStyle } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.btrix.in';

const ACTION_META = {
  CREATE:        { bg: 'rgba(34,197,94,0.15)',   text: '#4ade80',  icon: '✚' },
  READ:          { bg: 'rgba(14,165,233,0.15)',   text: '#38bdf8',  icon: '👁' },
  UPDATE:        { bg: 'rgba(234,179,8,0.15)',    text: '#fbbf24',  icon: '✎' },
  DELETE:        { bg: 'rgba(239,68,68,0.15)',    text: '#f87171',  icon: '✕' },
  ACCESS_DENIED: { bg: 'rgba(236,72,153,0.15)',   text: '#f9a8d4',  icon: '⛔' },
  LOGIN:         { bg: 'rgba(139,92,246,0.15)',   text: '#c084fc',  icon: '🔑' },
};

const STATUS_COLORS = {
  success: { bg: 'rgba(34,197,94,0.15)',  text: '#4ade80' },
  denied:  { bg: 'rgba(239,68,68,0.15)',  text: '#f87171' },
  failed:  { bg: 'rgba(245,158,11,0.15)', text: '#fbbf24' },
};

const RESOURCE_ICONS = { patient: '👤', appointment: '📅', staff: '👔', hospital: '🏥', visit: '🏥', prescription: '💊', lab_report: '🧪', notification: '💬' };

function ActionBadge({ action }) {
  const m = ACTION_META[action] || { bg: 'rgba(100,116,139,0.15)', text: '#94a3b8', icon: '•' };
  return <span style={{ background: m.bg, color: m.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{m.icon} {action}</span>;
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.success;
  return <span style={{ background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{status}</span>;
}

export default function AuditLogs({ user }) {
  const isSuperAdmin = user?.role === 'super-admin';
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [hospitals, setHospitals]   = useState([]);
  const [selectedHospital, setSelectedHospital] = useState(isSuperAdmin ? '' : user?.hospital_id);
  const today   = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7*24*3600*1000).toISOString().split('T')[0];
  const [startDate, setStartDate]   = useState(weekAgo);
  const [endDate, setEndDate]       = useState(today);
  const [actionFilter, setActionFilter]     = useState('all');
  const [resourceFilter, setResourceFilter] = useState('all');
  const [search, setSearch]         = useState('');

  const fetchHospitals = useCallback(async () => {
    try { const headers = await getAuthHeader(); setHospitals(parseResponse(await axios.get(`${API_URL}/hospitals`, { headers })).hospitals || []); } catch {}
  }, []);

  const fetchLogs = useCallback(async () => {
    const hid = selectedHospital || user?.hospital_id;
    if (!hid && !isSuperAdmin) return;
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const params  = { limit: 200 };
      if (hid)       params.hospital_id = hid;
      if (startDate) params.start_date  = startDate;
      if (endDate)   params.end_date    = endDate + 'T23:59:59';
      setLogs(parseResponse(await axios.get(`${API_URL}/hospitals/audit`, { headers, params })).logs || []);
    } catch {} finally { setLoading(false); }
  }, [selectedHospital, startDate, endDate, isSuperAdmin, user]);

  useEffect(() => { if (isSuperAdmin) fetchHospitals(); }, []); // eslint-disable-line
  useEffect(() => { fetchLogs(); }, [selectedHospital, startDate, endDate]); // eslint-disable-line

  const filtered = logs.filter(l => {
    const matchAction   = actionFilter   === 'all' || l.action   === actionFilter;
    const matchResource = resourceFilter === 'all' || l.resource === resourceFilter;
    const matchSearch   = !search || l.staff_id?.includes(search) || l.resource_id?.includes(search) || l.details?.includes(search);
    return matchAction && matchResource && matchSearch;
  });

  const counts = {
    total:   logs.length,
    denied:  logs.filter(l => l.status === 'denied').length,
    creates: logs.filter(l => l.action === 'CREATE').length,
    reads:   logs.filter(l => l.action === 'READ').length,
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>Audit Logs</div>
        <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>All data access and changes — retained for 90 days</div>
      </div>

      {/* Stats */}
      <div className="grid-4" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Events',    value: counts.total,   grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
          { label: 'Access Denied',   value: counts.denied,  grad: 'linear-gradient(135deg,#ef4444,#dc2626)' },
          { label: 'Creates',         value: counts.creates, grad: 'linear-gradient(135deg,#22c55e,#16a34a)' },
          { label: 'Reads',           value: counts.reads,   grad: 'linear-gradient(135deg,#8b5cf6,#ec4899)' },
        ].map((s, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: s.grad, borderRadius: '12px 12px 0 0' }} />
            <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1, marginBottom: 6, marginTop: 8 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#475569' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {isSuperAdmin && (
          <select style={filterInputStyle} value={selectedHospital} onChange={e => setSelectedHospital(e.target.value)}>
            <option value="">All hospitals</option>
            {hospitals.map(h => <option key={h.hospital_id} value={h.hospital_id}>{h.name}</option>)}
          </select>
        )}
        <input type="date" style={{ ...filterInputStyle, colorScheme: 'dark' }} value={startDate} onChange={e => setStartDate(e.target.value)} />
        <input type="date" style={{ ...filterInputStyle, colorScheme: 'dark' }} value={endDate}   onChange={e => setEndDate(e.target.value)} />
        <select style={filterInputStyle} value={actionFilter} onChange={e => setActionFilter(e.target.value)}>
          <option value="all">All actions</option>
          {Object.keys(ACTION_META).map(a => <option key={a}>{a}</option>)}
        </select>
        <select style={filterInputStyle} value={resourceFilter} onChange={e => setResourceFilter(e.target.value)}>
          <option value="all">All resources</option>
          {Object.keys(RESOURCE_ICONS).map(r => <option key={r}>{r}</option>)}
        </select>
        <input placeholder="Search staff ID, resource ID…" style={{ ...filterInputStyle, width: 200 }} value={search} onChange={e => setSearch(e.target.value)} />
        <button onClick={fetchLogs} style={{ padding: '8px 16px', borderRadius: 8, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          Refresh
        </button>
      </div>

      {/* Security alert */}
      {counts.denied > 0 && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#f87171' }}>{counts.denied} Access Denied Event{counts.denied > 1 ? 's' : ''} detected</div>
            <div style={{ fontSize: 12, color: '#ef4444' }}>Filter by ACCESS_DENIED to review unauthorized access attempts</div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="page-card">
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>Loading audit logs…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#334155', fontSize: 13 }}>No audit logs found</div>
        ) : (
          <div className="table-wrap">
            <table className="dark-table">
              <thead><tr>{['Timestamp','Staff ID','Action','Resource','Resource ID','Details','Status'].map(h => <th key={h}>{h}</th>)}</tr></thead>
              <tbody>
                {filtered.map((l, i) => {
                  let details = {};
                  try { details = JSON.parse(l.details || '{}'); } catch {}
                  const isDenied = l.status === 'denied';
                  return (
                    <tr key={i} style={{ background: isDenied ? 'rgba(239,68,68,0.05)' : undefined }}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 500 }}>{new Date(l.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
                        <div style={{ fontSize: 11, color: '#475569' }}>{new Date(l.timestamp).toLocaleDateString('en-IN')}</div>
                      </td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--accent-light)' }}>{l.staff_id}</td>
                      <td><ActionBadge action={l.action} /></td>
                      <td style={{ fontSize: 12, color: '#94a3b8' }}>{RESOURCE_ICONS[l.resource] || '•'} {l.resource}</td>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', color: '#475569' }}>{l.resource_id || '—'}</td>
                      <td style={{ maxWidth: 200 }}>
                        {Object.entries(details).slice(0, 3).map(([k, v]) => (
                          <span key={k} style={{ display: 'inline-block', background: 'var(--bg-card-hover)', borderRadius: 4, padding: '1px 6px', margin: '1px', fontSize: 10, color: 'var(--text-muted)' }}>
                            {k}: {String(v).slice(0, 30)}
                          </span>
                        ))}
                      </td>
                      <td><StatusBadge status={l.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
